import "./ScrollNavigation.scss";
import { gsap } from "gsap";
import { define, mount } from "@shared/Lib/registry";
import type { Component } from "@shared/Lib/types";

interface SetTopicEvent extends CustomEvent {
  detail: { topic: string; direction: "forward" | "backward" };
}

const VISIBLE_PREV = 3;
const TOTAL_SLOTS = VISIBLE_PREV + 2;
const SLOT_GAP = 10;
const DUR = 0.25;

const CUR_X = -5;
const PREV_X = -20;

type Positions = [number, number, number, number, number];

class ScrollNavigation implements Component {
  readonly el: HTMLElement;
  private readonly scollSlideContainers: NodeListOf<Element> | null;
  private readonly contentEl: HTMLElement | null;
  private readonly blackLine: SVGPathElement | null;
  private readonly redLine: SVGPathElement | null;
  private readonly yellowLines: SVGPathElement[];
  private readonly svgEl: SVGSVGElement | null;

  private yellowTopLine: SVGPathElement | null = null;
  private yellowBaseY = 0;
  private yellowOffset = 0;
  private stepPx = 0;

  private slots: HTMLSpanElement[] = [];
  private drumHead = 0;

  private topicList: (string | null)[] = [];
  private activeIndex = VISIBLE_PREV - 1;

  private hasAnimated = false;
  private isReady = false;
  private waveTop = 0;

  private showTl: gsap.core.Timeline | null = null;
  private currentTopicTl: gsap.core.Timeline | null = null;
  private pendingTopicEvent: SetTopicEvent | null = null;
  private topicAnimating = false;

  private readonly onShow = () => this.show();
  private readonly onHide = () => this.hide();
  private readonly onSetTopic = (e: Event) => this.setTopic(e as SetTopicEvent);
  private readonly onResize = () => this.handleResize();

  constructor(el: HTMLElement) {
    this.el = el;
    this.scollSlideContainers = document.querySelectorAll(
      ".scrollytelling__slides"
    );
    this.contentEl = el.querySelector(".scroll-navigation__content");
    this.blackLine = el.querySelector(".scroll-navigation__line--black");
    this.redLine = el.querySelector(".scroll-navigation__line--red");
    this.yellowLines = Array.from(
      el.querySelectorAll<SVGPathElement>(".scroll-navigation__line--yellow")
    );
    this.yellowTopLine = this.yellowLines[0] ?? null;
    this.svgEl = el.querySelector("svg");
    this.scollSlideContainers?.forEach((container) => {
      container.classList.add("has-scrollnav");
    });

    mount(el);
    this.init();
  }

  private init(): void {
    const h = this.getContainerHeight();

    gsap.set(this.el, { opacity: 0 });
    gsap.set(this.yellowLines, { y: h, opacity: 0 });
    gsap.set(this.redLine, { x: 10, y: h, opacity: 0 });
    gsap.set(this.blackLine, { x: 20, y: h, opacity: 0 });

    this.buildTopicList();
    this.createSlots();

    window.addEventListener("scrollNavigation:show", this.onShow);
    window.addEventListener("scrollNavigation:hide", this.onHide);
    window.addEventListener("scrollNavigation:setTopic", this.onSetTopic);
    window.addEventListener("resize", this.onResize);
  }

  private getContainerHeight(): number {
    return this.el.getBoundingClientRect().height || window.innerHeight;
  }

  private getViewBoxHeight(): number {
    return this.svgEl?.viewBox?.baseVal?.height || 1200;
  }

  private buildTopicList(): void {
    const topics = Array.from(
      document.querySelectorAll<HTMLElement>("[data-scroll-topic]")
    )
      .map((el) => el.dataset.scrollTopic ?? "")
      .filter((t) => t.trim() !== "");

    const seen = new Set<string>();
    const unique = topics.filter((t) => !seen.has(t) && !!seen.add(t));

    const padding: null[] = Array(VISIBLE_PREV).fill(null);
    this.topicList = [...padding, ...unique];
  }

  private createSlots(): void {
    if (!this.contentEl) return;
    this.slots = [];

    for (let i = 0; i < TOTAL_SLOTS; i++) {
      const item = document.createElement("div");
      item.className =
        "scroll-navigation__topic scroll-navigation__topic--prev";
      item.removeAttribute("aria-current");
      this.contentEl.appendChild(item);
      this.slots.push(item);
      gsap.set(item, {
        position: "absolute",
        opacity: 0,
        x: PREV_X,
        yPercent: -50,
      });
    }
  }

  private ensureSlotsReady(): boolean {
    return this.slots.length === TOTAL_SLOTS;
  }

  private slotAt(pos: number): HTMLSpanElement {
    const idx = (this.drumHead + pos) % TOTAL_SLOTS;
    const slot = this.slots[idx];
    if (!slot) {
      throw new Error("[ScrollNavigation] slotAt: slots not initialized");
    }
    return slot;
  }

  private computePositions(): Positions {
    let currentH = 24;
    let prevH = 16;

    if (this.slots.length > 0 && this.slots[0]) {
      const probe = this.slots[0];
      const savedClass = probe.className;
      const savedText = probe.textContent ?? "";

      probe.textContent = "\u00A0";
      probe.className =
        "scroll-navigation__topic scroll-navigation__topic--current";
      currentH = probe.offsetHeight || 24;

      probe.className =
        "scroll-navigation__topic scroll-navigation__topic--prev";
      prevH = probe.offsetHeight || 16;

      probe.className = savedClass;
      probe.textContent = savedText;
    }

    this.stepPx = prevH + SLOT_GAP;

    const p0 = this.waveTop - this.stepPx * 3;
    const p1 = p0 + this.stepPx;
    const p2 = p1 + this.stepPx;
    const p3 = this.waveTop;
    const p4 = p3 + currentH + SLOT_GAP;

    return [p0, p1, p2, p3, p4];
  }

  private shiftYellowTop(
    direction: "forward" | "backward",
    tl: gsap.core.Timeline
  ): void {
    if (!this.yellowTopLine) return;

    this.yellowOffset += direction === "forward" ? -this.stepPx : this.stepPx;

    tl.to(
      this.yellowTopLine,
      {
        y: this.yellowBaseY + this.yellowOffset,
        duration: DUR,
        ease: "power2.inOut",
      },
      0
    );
  }

  private getWaveTop(): number {
    const headerEl = document.querySelector<HTMLElement>(".summit-header");
    const headerHeight = headerEl?.offsetHeight ?? 0;
    return headerHeight + 150;
  }

  private getSvgHeight(): number {
    return this.svgEl?.getBoundingClientRect().height ?? 1200;
  }

  private onTopicAnimComplete(): void {
    this.topicAnimating = false;
    this.currentTopicTl = null;
    this.processTopicQueue();
  }

  private processTopicQueue(): void {
    const e = this.pendingTopicEvent;
    if (!e) return;
    this.pendingTopicEvent = null;
    this.executeSetTopic(e);
  }

  private show(): void {
    this.el.setAttribute("aria-current", "location");

    if (this.hasAnimated) {
      gsap.to(this.el, { opacity: 1, duration: 0.4, ease: "power2.out" });
      return;
    }
    this.hasAnimated = true;
    this.isReady = true;

    this.waveTop = this.getWaveTop();

    const containerH = this.getSvgHeight();
    const viewBoxH = this.getViewBoxHeight();
    const naturalWaveY = (80 / viewBoxH) * containerH;
    const waveTargetY = this.waveTop - naturalWaveY;

    this.yellowBaseY = waveTargetY;
    this.yellowOffset = 0;

    this.slots.forEach((s) => gsap.set(s, { top: this.waveTop }));

    const allLines = [this.blackLine, this.redLine, ...this.yellowLines].filter(
      Boolean
    );

    const tl = gsap.timeline({
      onComplete: () => {
        this.showTl = null;
        this.processTopicQueue();
      },
    });
    this.showTl = tl;

    tl.to(this.el, { opacity: 1, duration: 0.3, ease: "power2.out" }, 0);
    tl.to(
      this.yellowLines,
      { opacity: 1, duration: 0.3, ease: "power2.out" },
      0
    );
    tl.to(
      this.blackLine,
      { x: 0, opacity: 1, duration: 0.45, ease: "power2.out" },
      0
    );
    tl.to(
      this.redLine,
      { x: 0, opacity: 1, duration: 0.4, ease: "power2.out" },
      0.05
    );
    tl.to(allLines, { y: waveTargetY, duration: 0.4, ease: "power2.out" }, 0.1);
  }

  private hide(): void {
    gsap.to(this.el, { opacity: 0, duration: 0.4, ease: "power1.inOut" });
  }

  private handleResize(): void {
    if (!this.isReady) return;

    this.waveTop = this.getWaveTop();

    const containerH = this.getSvgHeight();
    const viewBoxH = this.getViewBoxHeight();
    const naturalWaveY = (80 / viewBoxH) * containerH;
    const waveTargetY = this.waveTop - naturalWaveY;

    const pos = this.computePositions();
    const prevCount = Math.min(
      Math.max(this.activeIndex - VISIBLE_PREV, 0),
      VISIBLE_PREV
    );

    this.yellowBaseY = waveTargetY;
    this.yellowOffset = -prevCount * this.stepPx;

    const allLines = [this.blackLine, this.redLine, ...this.yellowLines].filter(
      Boolean
    );
    gsap.set(allLines, { y: waveTargetY });

    if (this.yellowTopLine) {
      gsap.set(this.yellowTopLine, { y: this.yellowBaseY + this.yellowOffset });
    }

    if (this.ensureSlotsReady()) {
      for (let p = 0; p < TOTAL_SLOTS; p++) {
        gsap.set(this.slotAt(p), { top: pos[p]! });
      }
    }
  }

  private setTopic(e: SetTopicEvent): void {
    if (!this.isReady) return;

    const { topic, direction } = e.detail;
    const topicIndex = this.topicList.indexOf(topic);
    if (topicIndex === -1) return;

    const newActiveIndex =
      direction === "forward" ? topicIndex : topicIndex - 1;
    if (newActiveIndex < 0 || newActiveIndex === this.activeIndex) return;

    this.pendingTopicEvent = e;

    if (this.showTl) {
      return;
    }

    if (this.topicAnimating && this.currentTopicTl) {
      this.currentTopicTl.progress(1);
      return;
    }

    this.processTopicQueue();
  }

  private executeSetTopic(e: SetTopicEvent): void {
    const { topic, direction } = e.detail;
    const topicIndex = this.topicList.indexOf(topic);
    if (topicIndex === -1) return;

    const prevActiveIndex = this.activeIndex;
    const newActiveIndex =
      direction === "forward" ? topicIndex : topicIndex - 1;

    if (newActiveIndex < 0 || newActiveIndex === this.activeIndex) return;

    const prevPrevCount = Math.min(
      Math.max(prevActiveIndex - VISIBLE_PREV, 0),
      VISIBLE_PREV
    );
    const newPrevCount = Math.min(
      Math.max(newActiveIndex - VISIBLE_PREV, 0),
      VISIBLE_PREV
    );
    const shiftYellow = prevPrevCount !== newPrevCount;

    this.activeIndex = newActiveIndex;
    this.topicAnimating = true;

    if (direction === "forward") {
      prevActiveIndex < VISIBLE_PREV
        ? this.animateFirstIn(shiftYellow)
        : this.animateForward(shiftYellow);
    } else {
      newActiveIndex < VISIBLE_PREV
        ? this.animateLastOut(shiftYellow)
        : this.animateBackward(shiftYellow);
    }
  }

  private animateFirstIn(shiftYellow: boolean): void {
    if (!this.ensureSlotsReady()) return;

    const pos = this.computePositions();
    const topic = this.topicList[this.activeIndex];

    for (let p = 0; p < TOTAL_SLOTS; p++) {
      if (p === 3) continue;
      const slot = this.slotAt(p);
      slot.textContent = "";
      slot.className =
        "scroll-navigation__topic scroll-navigation__topic--prev";
      slot.removeAttribute("aria-current");
      gsap.set(slot, { top: pos[p]!, opacity: 0, x: PREV_X });
    }

    const current = this.slotAt(3);
    current.textContent = topic ?? "";
    current.className =
      "scroll-navigation__topic scroll-navigation__topic--current";
    topic
      ? current.setAttribute("aria-current", "location")
      : current.removeAttribute("aria-current");

    const tl = gsap.timeline({ onComplete: () => this.onTopicAnimComplete() });
    this.currentTopicTl = tl;

    if (shiftYellow) this.shiftYellowTop("forward", tl);

    tl.fromTo(
      current,
      { top: pos[4], opacity: 0, x: CUR_X },
      {
        top: pos[3],
        opacity: topic ? 1 : 0,
        duration: DUR * 2,
        ease: "power2.out",
      },
      0
    );
  }

  private animateLastOut(shiftYellow: boolean): void {
    if (!this.ensureSlotsReady()) return;

    const pos = this.computePositions();
    const current = this.slotAt(3);

    const tl = gsap.timeline({
      onComplete: () => {
        current.textContent = "";
        current.className =
          "scroll-navigation__topic scroll-navigation__topic--prev";
        current.removeAttribute("aria-current");
        gsap.set(current, { x: PREV_X });
        this.onTopicAnimComplete();
      },
    });
    this.currentTopicTl = tl;

    if (shiftYellow) this.shiftYellowTop("backward", tl);

    tl.to(
      current,
      { top: pos[4], opacity: 0, duration: DUR, ease: "power2.inOut" },
      0
    );
  }

  private animateForward(shiftYellow: boolean): void {
    if (!this.ensureSlotsReady()) return;

    const pos = this.computePositions();
    const newTopic = this.topicList[this.activeIndex];

    const exitSlot = this.slotAt(0);
    const oldCurrentSlot = this.slotAt(3);
    const bufferSlot = this.slotAt(4);

    bufferSlot.textContent = newTopic ?? "";
    bufferSlot.className =
      "scroll-navigation__topic scroll-navigation__topic--current";
    newTopic
      ? bufferSlot.setAttribute("aria-current", "location")
      : bufferSlot.removeAttribute("aria-current");
    gsap.set(bufferSlot, { top: pos[4], opacity: 0, x: CUR_X });

    oldCurrentSlot.className =
      "scroll-navigation__topic scroll-navigation__topic--prev";

    const tl = gsap.timeline({
      onComplete: () => {
        gsap.set(exitSlot, { top: pos[4], opacity: 0, x: PREV_X });
        exitSlot.textContent = "";
        exitSlot.removeAttribute("aria-current");
        this.drumHead = (this.drumHead + 1) % TOTAL_SLOTS;
        this.onTopicAnimComplete();
      },
    });
    this.currentTopicTl = tl;

    if (shiftYellow) this.shiftYellowTop("forward", tl);

    tl.to(
      exitSlot,
      {
        top: pos[0] - (pos[1] - pos[0]),
        opacity: 0,
        duration: DUR,
        ease: "power2.inOut",
      },
      0
    );

    for (let p = 1; p <= 3; p++) {
      tl.to(
        this.slotAt(p),
        { top: pos[p - 1]!, duration: DUR, ease: "power2.inOut" },
        0
      );
    }

    tl.to(
      oldCurrentSlot,
      { x: PREV_X, duration: DUR, ease: "power2.inOut" },
      0
    );

    tl.to(
      bufferSlot,
      {
        top: pos[3],
        opacity: newTopic ? 1 : 0,
        duration: DUR,
        ease: "power2.inOut",
      },
      0
    );
  }

  private animateBackward(shiftYellow: boolean): void {
    if (!this.ensureSlotsReady()) return;

    const pos = this.computePositions();

    const incomingPrev3 =
      this.topicList[this.activeIndex - VISIBLE_PREV] ?? null;
    const aboveBuffer = this.slotAt(4);
    const exitSlot = this.slotAt(3);
    const newCurrentSlot = this.slotAt(2);

    aboveBuffer.textContent = incomingPrev3 ?? "";
    aboveBuffer.className =
      "scroll-navigation__topic scroll-navigation__topic--prev";
    incomingPrev3
      ? aboveBuffer.setAttribute("aria-current", "location")
      : aboveBuffer.removeAttribute("aria-current");
    gsap.set(aboveBuffer, {
      top: pos[0] - (pos[1] - pos[0]),
      opacity: 0,
      x: PREV_X,
    });

    newCurrentSlot.className =
      "scroll-navigation__topic scroll-navigation__topic--prev";
    if (newCurrentSlot.textContent?.trim())
      newCurrentSlot.setAttribute("aria-current", "location");
    gsap.set(newCurrentSlot, { x: PREV_X });

    const tl = gsap.timeline({
      onComplete: () => {
        gsap.set(exitSlot, { top: pos[4], opacity: 0, x: PREV_X });
        exitSlot.textContent = "";
        exitSlot.className =
          "scroll-navigation__topic scroll-navigation__topic--prev";
        exitSlot.removeAttribute("aria-current");
        this.drumHead = (this.drumHead - 1 + TOTAL_SLOTS) % TOTAL_SLOTS;
        this.onTopicAnimComplete();
      },
    });
    this.currentTopicTl = tl;

    if (shiftYellow) this.shiftYellowTop("backward", tl);

    tl.to(
      aboveBuffer,
      {
        top: pos[0],
        opacity: incomingPrev3 ? 1 : 0,
        duration: DUR,
        ease: "power2.inOut",
      },
      0
    );

    for (let p = 0; p <= 1; p++) {
      tl.to(
        this.slotAt(p),
        { top: pos[p + 1]!, duration: DUR, ease: "power2.inOut" },
        0
      );
    }

    tl.to(
      newCurrentSlot,
      { top: pos[3]!, x: CUR_X, duration: DUR, ease: "power2.inOut" },
      0
    );

    tl.call(
      () => {
        exitSlot.className =
          "scroll-navigation__topic scroll-navigation__topic--prev";
        newCurrentSlot.className =
          "scroll-navigation__topic scroll-navigation__topic--current";
        if (newCurrentSlot.textContent?.trim())
          newCurrentSlot.setAttribute("aria-current", "location");
      },
      [],
      DUR * 0.55
    );

    tl.to(
      exitSlot,
      { x: PREV_X, opacity: 0.9, duration: DUR, ease: "power2.inOut" },
      0
    );
  }

  destroy(): void {
    window.removeEventListener("scrollNavigation:show", this.onShow);
    window.removeEventListener("scrollNavigation:hide", this.onHide);
    window.removeEventListener("scrollNavigation:setTopic", this.onSetTopic);
    window.removeEventListener("resize", this.onResize);

    gsap.killTweensOf(
      [
        this.el,
        this.blackLine,
        this.redLine,
        ...this.yellowLines,
        ...this.slots,
      ].filter(Boolean)
    );
  }
}

define("scroll-navigation", (el) => new ScrollNavigation(el));
export { ScrollNavigation };
